import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ============================================================================
// #296 Particle Trail Visualization (粒子轨迹可视化)
// ============================================================================
// 技术要点：
// - 优化粒子系统（120 个粒子）
// - 轨迹连线网络（距离检测）
// - 3D 透视效果（深度模拟）
// - Spring 物理动画
// - 适用：数据可视化、科技主题、背景装饰
// ============================================================================

export const ParticleTrailVisualization: React.FC<{
  particleCount?: number;
  primaryColor?: string;
  secondaryColor?: string;
}> = ({
  particleCount = 120,
  primaryColor = '#00d9ff',
  secondaryColor = '#ff006e',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  interface Particle {
    id: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    color: string;
    trail: Array<{ x: number; y: number }>;
  }

  // 生成粒子
  const particles: Particle[] = [];
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2;
    const radius = 200 + Math.sin(angle * 3 + frame * 0.02) * 100;
    const speed = 0.02 + Math.random() * 0.01;
    
    const x = width / 2 + Math.cos(angle + frame * speed) * radius;
    const y = height / 2 + Math.sin(angle + frame * speed) * radius;
    
    // 轨迹点（最近 10 个位置）
    const trail = [];
    for (let t = 0; t < 10; t++) {
      const pastFrame = Math.max(0, frame - t * 3);
      const pastAngle = (i / particleCount) * Math.PI * 2;
      const pastRadius = 200 + Math.sin(pastAngle * 3 + pastFrame * 0.02) * 100;
      trail.push({
        x: width / 2 + Math.cos(pastAngle + pastFrame * speed) * pastRadius,
        y: height / 2 + Math.sin(pastAngle + pastFrame * speed) * pastRadius,
      });
    }

    particles.push({
      id: i,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 4 + Math.sin(frame * 0.05 + i) * 2,
      color: i % 2 === 0 ? primaryColor : secondaryColor,
      trail,
    });
  }

  // 连线（距离检测）
  const lines: Array<{ x1: number; y1: number; x2: number; y2: number; opacity: number }> = [];
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dx = particles[i].x - particles[j].x;
      const dy = particles[i].y - particles[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 80) {
        const opacity = interpolate(distance, [0, 80], [0.3, 0]);
        lines.push({
          x1: particles[i].x,
          y1: particles[i].y,
          x2: particles[j].x,
          y2: particles[j].y,
          opacity,
        });
      }
    }
  }

  // 中心文字动画
  const textOpacity = spring({ frame, fps, config: { damping: 200 } });
  const textScale = spring({ frame, fps, config: { damping: 15, stiffness: 100 } });

  return (
    <AbsoluteFill style={{ background: 'radial-gradient(circle at 50% 50%, #1a0a2e 0%, #0a0a1a 100%)' }}>
      {/* 连线 */}
      <svg width={width} height={height}>
        {lines.map((line, i) => (
          <line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={primaryColor}
            strokeWidth="1"
            opacity={line.opacity}
          />
        ))}
      </svg>

      {/* 粒子轨迹 */}
      {particles.map((particle) => (
        <React.Fragment key={particle.id}>
          {/* 轨迹线 */}
          <svg
            width={width}
            height={height}
            style={{ position: 'absolute', left: 0, top: 0 }}
          >
            <path
              d={`M ${particle.trail.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}`}
              stroke={particle.color}
              strokeWidth="2"
              fill="none"
              opacity={0.3}
            />
          </svg>
          
          {/* 粒子 */}
          <div
            style={{
              position: 'absolute',
              left: particle.x - particle.size / 2,
              top: particle.y - particle.size / 2,
              width: particle.size,
              height: particle.size,
              borderRadius: '50%',
              backgroundColor: particle.color,
              boxShadow: `0 0 ${particle.size * 3}px ${particle.color}`,
            }}
          />
        </React.Fragment>
      ))}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${textScale})`,
          opacity: textOpacity,
          fontFamily: 'Arial, sans-serif',
          fontSize: 80,
          fontWeight: 'bold',
          color: '#ffffff',
          textShadow: `0 0 40px ${primaryColor}`,
          zIndex: 10,
        }}
      >
        TRAIL
      </div>
    </AbsoluteFill>
  );
};

// ============================================================================
// #298 Generative Art Grid (生成艺术网格)
// ============================================================================
// 技术要点：
// - 数学函数驱动（sin/cos/perlin-like）
// - 20x20 网格（400 个点）
// - 动态颜色渐变（HSL）
// - 分形元素（递归图案）
// - Spring 物理动画
// - 适用：艺术视频、背景装饰、数学可视化
// ============================================================================

export const GenerativeArtGrid: React.FC<{
  gridSize?: number;
  color1?: string;
  color2?: string;
}> = ({
  gridSize = 20,
  color1 = '#00ffff',
  color2 = '#ff00ff',
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const cellWidth = width / gridSize;
  const cellHeight = height / gridSize;

  interface GridCell {
    x: number;
    y: number;
    size: number;
    color: string;
    rotation: number;
  }

  // 生成网格单元
  const cells: GridCell[] = [];
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const x = col * cellWidth + cellWidth / 2;
      const y = row * cellHeight + cellHeight / 2;
      
      // Perlin-like 噪声模拟
      const noise = Math.sin(col * 0.3 + frame * 0.05) * Math.cos(row * 0.3 + frame * 0.05);
      const size = 10 + noise * 20 + Math.sin(frame * 0.03 + row + col) * 10;
      
      // HSL 颜色渐变
      const hue = (frame + col * 5 + row * 5) % 360;
      const color = `hsl(${hue}, 70%, 60%)`;
      
      const rotation = noise * 45 + frame;
      
      cells.push({ x, y, size, color, rotation });
    }
  }

  // 中心文字动画
  const textOpacity = spring({ frame, fps, config: { damping: 200 } });
  const textScale = spring({ frame, fps, config: { damping: 15, stiffness: 100 } });

  // 分形装饰（递归圆形）
  const fractalCircles: React.ReactElement[] = [];
  const drawFractalCircle = (x: number, y: number, radius: number, depth: number) => {
    if (depth === 0 || radius < 5) return;
    
    fractalCircles.push(
      <div
        key={`fractal-${x}-${y}-${depth}`}
        style={{
          position: 'absolute',
          left: x - radius,
          top: y - radius,
          width: radius * 2,
          height: radius * 2,
          border: `2px solid ${color1}`,
          borderRadius: '50%',
          opacity: 0.2,
          transform: `rotate(${frame * (5 - depth)}deg)`,
        }}
      />
    );
    
    // 递归调用（4 个方向）
    const newRadius = radius * 0.5;
    drawFractalCircle(x - radius / 2, y, newRadius, depth - 1);
    drawFractalCircle(x + radius / 2, y, newRadius, depth - 1);
    drawFractalCircle(x, y - radius / 2, newRadius, depth - 1);
    drawFractalCircle(x, y + radius / 2, newRadius, depth - 1);
  };
  
  drawFractalCircle(width / 2, height / 2, 200, 4);

  return (
    <AbsoluteFill style={{ background: 'radial-gradient(circle at 50% 50%, #0a0a2e 0%, #000000 100%)' }}>
      {/* 分形装饰 */}
      {fractalCircles}

      {/* 网格单元 */}
      <svg width={width} height={height}>
        {cells.map((cell, i) => (
          <rect
            key={i}
            x={cell.x - cell.size / 2}
            y={cell.y - cell.size / 2}
            width={cell.size}
            height={cell.size}
            fill={cell.color}
            opacity={0.6}
            transform={`rotate(${cell.rotation} ${cell.x} ${cell.y})`}
          />
        ))}
      </svg>

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${textScale})`,
          opacity: textOpacity,
          fontFamily: 'Arial, sans-serif',
          fontSize: 100,
          fontWeight: 'bold',
          color: '#ffffff',
          textShadow: `0 0 40px ${color1}, 0 0 80px ${color2}`,
          zIndex: 10,
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '20px 40px',
          borderRadius: 10,
        }}
      >
        GENERATIVE
      </div>

      {/* 参数显示 */}
      {/* <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 30,
          fontFamily: 'monospace',
          fontSize: 20,
          color: color1,
          opacity: 0.6,
        }}
      >
        Grid: {gridSize}x{gridSize} | Cells: {gridSize * gridSize}
      </div> */}
    </AbsoluteFill>
  );
};
